import { LoadingOverlay } from '@mantine/core'
import type { SharedTypes } from '@semios/app-platform-banyan-route-definitions'
import { Button } from 'components/Button/Button'
import { translate } from 'i18n/i18n'
import { useCallback, useEffect, useState } from 'react'
import { colors } from 'settings/colors'
import { apiFetch } from 'utils/apiFetch'
import { showNotification } from 'utils/showNotification'
import { useIsWideScreen } from 'utils/useIsWideScreen'
import { SettingsCreateNewButton } from '../../../components/SettingsCreateNewButton/SettingsCreateNewButton'
import { SettingsFooterContainer } from '../../../components/SettingsFooterContainer/SettingsFooterContainer'
import { ItemHeader } from '../../../WideScreen/ItemHeader/ItemHeader'
import { OnDemandIrrigationReportModalDrawer } from '../Reports/OnDemandReportModalDrawer'
import { IrrigationReportsTable } from './IrrigationReportsTable'

export const IrrigationReportsMain = () => {
  const [modalOpened, setModalOpened] = useState(false)
  const [reports, setReports] = useState<SharedTypes.TOnDemandIrrigationReportWithProperty[]>([])
  const [loading, setLoading] = useState(false)
  const isWideScreen = useIsWideScreen()

  const fetchReports = useCallback(async ({ silent = false }: { silent?: boolean } = {}) => {
    if (!silent) setLoading(true)

    try {
      const response = await apiFetch({ url: '/on-demand-irrigation-reports-get', body: {} })

      setReports(response.reports)
    } catch (err) {
      if (!silent) setReports([])
    } finally {
      if (!silent) setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchReports()
  }, [fetchReports])

  const hasPendingReports = reports.some((report) => report.status === 'pending')

  useEffect(() => {
    if (!hasPendingReports) return

    const interval = setInterval(() => {
      fetchReports({ silent: true })
    }, 15000)

    return () => clearInterval(interval)
  }, [hasPendingReports, fetchReports])

  const handleRetry = async (reportId: string) => {
    try {
      const response = await apiFetch({
        url: '/on-demand-irrigation-report-retry',
        body: { reportId },
      })

      if (response.success) {
        showNotification({
          type: 'success',
          message: translate.phrases.banyanApp('Report generation restarted'),
        })

        await fetchReports()
      } else if (response.errorCode === 'TOO_MANY_PENDING_REQUESTS') {
        showNotification({
          type: 'error',
          message: translate.phrases.banyanApp(
            'You have too many reports in progress. Please wait for them to finish before creating another.',
          ),
        })
      } else {
        showNotification({
          type: 'error',
          message: translate.phrases.banyanApp('Failed to retry the report. Please try again.'),
        })
      }
    } catch (err) {
      showNotification({
        type: 'error',
        message: translate.phrases.banyanApp('Failed to retry the report. Please try again.'),
      })
    }
  }

  const handleDelete = async (reportId: string) => {
    try {
      const response = await apiFetch({
        url: '/on-demand-irrigation-report-delete',
        body: { reportId },
      })

      if (response.success) {
        showNotification({
          type: 'success',
          message: translate.phrases.banyanApp('Report successfully deleted'),
        })

        await fetchReports()
      } else {
        showNotification({
          type: 'error',
          message: translate.phrases.banyanApp('Failed to delete the report. Please try again.'),
        })
      }
    } catch (err) {
      showNotification({
        type: 'error',
        message: translate.phrases.banyanApp('Failed to delete the report. Please try again.'),
      })
    }
  }

  const handleDownload = async (reportId: string) => {
    try {
      const response = await apiFetch({
        url: '/on-demand-irrigation-report-download',
        body: { reportId },
      })

      if (response.url) {
        window.open(response.url, '_blank')
      } else {
        showNotification({
          type: 'error',
          message: translate.phrases.banyanApp('The report is not available for download yet.'),
        })
      }
    } catch (err) {
      showNotification({
        type: 'error',
        message: translate.phrases.banyanApp('Failed to download the report. Please try again.'),
      })
    }
  }

  return (
    <>
      <LoadingOverlay visible={loading} />
      <ItemHeader>{translate.phrases.banyanApp('Irrigation Report')}</ItemHeader>
      <div css={{ margin: '0px 20px', paddingBottom: 60 }}>
        {isWideScreen && (
          <div css={{ textAlign: 'right', marginBottom: 20 }}>
            <SettingsCreateNewButton
              label={translate.phrases.banyanApp('Irrigation Report')}
              onClick={() => {
                setModalOpened(true)
              }}
            />
          </div>
        )}
        {!loading && reports.length === 0 ? (
          <div
            css={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              marginTop: 20,
              borderTop: `1px solid ${colors.grey200}`,
              padding: 20,
              marginBottom: 5,
            }}
          >
            {translate.phrases.banyanApp('No reports generated.')}
            <Button
              variant="link"
              onClick={() => {
                setModalOpened(true)
              }}
            >
              {translate.phrases.banyanApp('Create report now')}
            </Button>
          </div>
        ) : (
          <IrrigationReportsTable
            reports={reports}
            onDelete={handleDelete}
            onDownload={handleDownload}
            onRetry={handleRetry}
          />
        )}
      </div>
      {!isWideScreen && (
        <SettingsFooterContainer>
          <SettingsCreateNewButton
            label={translate.phrases.banyanApp('Irrigation Report')}
            onClick={() => {
              setModalOpened(true)
            }}
            css={{ width: '100%' }}
          />
        </SettingsFooterContainer>
      )}
      <OnDemandIrrigationReportModalDrawer
        opened={modalOpened}
        onClose={() => {
          setModalOpened(false)
        }}
        onCreated={fetchReports}
      />
    </>
  )
}
